import { Link, useNavigate } from 'react-router-dom';
import { Menu, User, LogOut, Package, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getAuthConfig, clearAuth } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('bluecrate_session_token');
    if (!token) return;
    axios.get(`${BACKEND_URL}/api/auth/me`, getAuthConfig())
      .then(res => setUser(res.data))
      .catch(() => {
        clearAuth();
        setUser(null);
      });
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, getAuthConfig());
    } catch {}
    clearAuth();
    setUser(null);
    setProfileOpen(false);
    navigate('/');
  };

  const navLinks = [
    { to: '/', label: 'Home', testId: 'nav-home' },
    { to: '/products', label: 'Products', testId: 'nav-products' },
    { to: '/membership', label: 'Membership', testId: 'nav-membership' },
    { to: '/faq', label: 'FAQ', testId: 'nav-faq' },
    { to: '/contact', label: 'Contact', testId: 'nav-contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="/bluecrate-logo-cropped.jpg"
              alt="BlueCrate"
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map(l => (
              <Link key={l.to} to={l.to}
                className="text-gray-700 hover:text-primary font-medium transition-colors"
                data-testid={l.testId}
              >
                {l.label}
              </Link>
            ))}

            {/* Profile / Sign In */}
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileOpen(p => !p)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 hover:border-primary hover:bg-primary/5 transition-all"
                  data-testid="profile-menu-btn"
                >
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
                    {(user.name || user.email || 'U')[0].toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
                    {user.name || user.email}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>

                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-xs text-gray-500">Signed in as</p>
                      <p className="text-sm font-semibold text-gray-800 truncate">{user.email}</p>
                    </div>
                    <Link
                      to="/my-orders"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setProfileOpen(false)}
                      data-testid="dropdown-my-orders"
                    >
                      <Package className="w-4 h-4 text-primary" />
                      My Orders
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      data-testid="dropdown-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                asChild
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="nav-sign-in-btn"
              >
                <Link to="/my-orders">
                  <User className="w-4 h-4 mr-2" />
                  Sign In
                </Link>
              </Button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-full hover:bg-gray-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map(l => (
              <Link key={l.to} to={l.to}
                className="block text-gray-700 hover:text-primary font-medium py-2 transition-colors"
                data-testid={`mobile-${l.testId}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <div className="border-t border-gray-100 pt-4">
              {user ? (
                <>
                  <div className="flex items-center gap-3 py-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold">
                      {(user.name || user.email || 'U')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{user.name || 'Account'}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <Link
                    to="/my-orders"
                    className="flex items-center gap-2 text-gray-700 hover:text-primary font-medium py-2 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-my-orders"
                  >
                    <Package className="w-4 h-4" />
                    My Orders
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 text-red-600 font-medium py-2 transition-colors w-full"
                    data-testid="mobile-nav-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/my-orders"
                  className="flex items-center gap-2 text-primary font-semibold py-2"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-sign-in"
                >
                  <User className="w-4 h-4" />
                  Sign In / My Orders
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
